/**
 * Component: PreregistrationReviewDialog ("Detalle del participante")
 * Everything about one registration in tabs — submitted data, documents, payments and receipts,
 * attendance — plus verify / reject. When opened from the "Revisar pendientes" queue it shows
 * Anterior / Siguiente and moves on to the next pending registration after each review.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Eye, ExternalLink, FileText, Loader2, MessageCircle, PenLine, Upload } from 'lucide-react';
import { needsSignature } from '../public/registrationEditLogic';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/supabase.config';
import {
  getParticipantPayments,
  PAYMENT_METHOD_LABELS,
  recordPayment,
  type EventPaymentMethod,
} from '@/Services/eventPayments.service';
import { formatPrice } from '@/models/event';
import { getReceiptById } from '@/features/receipts/data/receiptsRepository';
import { formatReceiptNumber, receiptToPrintData, type Receipt } from '@/features/receipts/types';
import { publicReceiptUrl } from '@/features/receipts/utils/publicReceiptUrl';
import { ReceiptPrintPreview } from '@/features/receipts/components/ReceiptPrintPreview';
import { WhatsAppMessageDialog } from '@/components/WhatsAppMessageDialog';
import { adminCreateEditLink, adminGetEditLink } from '../public/publicRegistrationRepository';
import { publicEditUrl, publicFormUrl } from '../public/publicUrls';
import { reviewMessage } from '../utils/reviewMessages';
import { eventReceiptShare } from '../utils/receiptShare';
import { participantPhone } from '../utils/participantPhone';
import {
  adminUpdateRegistration,
  replaceRegistrationDocument,
  uploadRegistrationDocument,
} from '../public/publicRegistrationRepository';
import { SignatureDialog } from './SignatureDialog';
import { RegistrationDocumentPanel } from './RegistrationDocumentPanel';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { compressImageForUpload } from '../public/compressImage';
import type { DocumentKind } from '../public/types';
import { eventParticipantsKeys } from '../hooks/useEventParticipants';
import { MinisterDataMergeSection } from './MinisterDataMergeSection';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';

const DOC_LABELS: Record<string, string> = {
  ci_front: 'CI anverso',
  ci_back: 'CI reverso',
  voucher: 'Comprobante de pago',
  delegation_letter: 'Carta de delegación',
  signature: 'Firma',
};

const FIELD_LABELS: [string, string][] = [
  ['full_name', 'Nombre completo'],
  ['dni', 'CI'],
  ['position', 'Cargo'],
  ['cell_phone', 'Celular'],
  ['office_phone', 'Tel. oficina'],
  ['email', 'E-mail'],
  ['address', 'Dirección'],
  ['birth_date', 'Fecha de nacimiento'],
  ['represented_name', 'Representa a'],
  ['represented_ci', 'CI del representado'],
  ['institution_note', 'Detalle institución'],
];

const REGISTRATION_BADGE = {
  PRE_REGISTERED: { text: 'Por revisar', variant: 'outline' },
  VERIFIED: { text: 'Verificada', variant: 'secondary' },
  REJECTED: { text: 'Rechazada', variant: 'destructive' },
} as const;

const METHOD_LABELS: Record<string, string> = { QR: 'QR', KIOSK: 'Kiosco', MANUAL: 'Manual' };

export interface ReviewQueue {
  /** 0-based position and size of the pending queue */
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  /** Called after verify / reject / reset instead of closing: moves to the next pending one */
  onReviewed: () => void;
}

interface PreregistrationReviewDialogProps {
  eventId: string;
  eventName?: string;
  participant: ParticipantWithDetails | null;
  onOpenChange: (open: boolean) => void;
  queue?: ReviewQueue | null;
  /** Event signature mode: a Digital registration without signature shows a warning */
  signatureMode?: string | null;
  /** Edit the form / send the edit link (only for staff who may register participants) */
  onEditForm?: (participant: ParticipantWithDetails) => void;
  onRequestLink?: (participant: ParticipantWithDetails) => void;
  /**
   * Called after a registration is verified. `recorded` is the payment recorded in the same step
   * (null when none), so the caller can offer the receipt right away.
   */
  onVerified?: (
    participant: ParticipantWithDetails,
    recorded: { paymentId: string; amount: number; method: EventPaymentMethod } | null,
  ) => void;
}

interface SessionAttendanceRow {
  checked_in_at: string;
  method: string;
  session: { name: string; session_date: string; start_time: string | null } | null;
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{children}</p>;
}

export function PreregistrationReviewDialog({
  eventId,
  eventName,
  participant,
  onOpenChange,
  queue,
  signatureMode,
  onEditForm,
  onRequestLink,
  onVerified,
}: PreregistrationReviewDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [alsoRecordPayment, setAlsoRecordPayment] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<DocumentKind | null>(null);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [tab, setTab] = useState('data');
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  // Documents replaced from this dialog (the participant prop is a snapshot taken when it opened)
  const [localDocs, setLocalDocs] = useState<Partial<Record<DocumentKind, string>> | null>(null);
  const [replacedNow, setReplacedNow] = useState<Set<string>>(new Set());
  const [uploadingKind, setUploadingKind] = useState<DocumentKind | null>(null);
  const [signatureOpen, setSignatureOpen] = useState(false);
  // WhatsApp message to the participant; `afterNotify` finishes the review once it is closed
  const [notify, setNotify] = useState<string | null>(null);
  const afterNotify = useRef<(() => void) | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingKind = useRef<DocumentKind | null>(null);

  // A different participant (queue moved on): start from a clean state
  const participantId = participant?.id;
  useEffect(() => {
    setShowReject(false);
    setRejectReason('');
    setAlsoRecordPayment(true);
    setTab('data');
    setLocalDocs(null);
    setReplacedNow(new Set());
    setSelectedDoc(null);
  }, [participantId]);

  const canViewPayments = can('event_payment.view');
  const { data: payments = [], isLoading: loadingPayments } = useQuery({
    queryKey: ['participant_payments', participantId],
    queryFn: () => getParticipantPayments(participantId as string),
    enabled: !!participantId && canViewPayments && tab === 'payments',
  });

  const { data: sessionAttendance = [], isLoading: loadingAttendance } = useQuery({
    queryKey: ['participant_session_attendance', participantId],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('event_session_attendance')
        .select('checked_in_at, method, session:session_id ( name, session_date, start_time )')
        .eq('participant_id', participantId)
        .order('checked_in_at', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as SessionAttendanceRow[];
    },
    enabled: !!participantId && tab === 'attendance',
  });

  if (!participant) return null;

  const data = (participant.registration_data ?? {}) as Record<string, unknown>;
  const documents = localDocs ?? participant.documents ?? {};
  const documentEntries = Object.entries(documents).filter(([, key]) => !!key);
  const name = participant.minister?.full_name ?? participant.guest?.full_name ?? 'Participante';
  const kind = (data.kind as string) ?? (participant.minister_id ? 'MINISTER' : participant.guest?.is_delegate ? 'DELEGATE' : 'GUEST');
  const paymentPending = participant.payment_status !== 'PAID';
  const status = REGISTRATION_BADGE[participant.registration_status];
  const price = Number(participant.calculated_price ?? 0);
  const paid = Number(participant.payment_amount ?? 0);

  const close = () => {
    setShowReject(false);
    setRejectReason('');
    onOpenChange(false);
  };

  // Documents a registration of this kind is expected to have, plus any extra one present
  const expectedDocs: DocumentKind[] = [
    ...(kind === 'DELEGATE' ? (['delegation_letter', 'voucher'] as const) : (['ci_front', 'ci_back', 'voucher'] as const)),
    // A Digital-signature event expects the drawn signature too (shown as "Falta" until it exists)
    ...(signatureMode === 'DIGITAL' ? (['signature'] as const) : []),
  ];
  const docRows = [
    ...expectedDocs,
    ...(Object.keys(documents) as DocumentKind[]).filter((k) => !expectedDocs.includes(k) && documents[k]),
  ];
  // A staff upload that replaced a file ("Actualizado") vs one that added a missing file ("Subido")
  const staffEntries = participant.document_replacements ?? [];
  const replacedKinds = new Set([...staffEntries.filter((r) => r.old_key).map((r) => r.kind), ...replacedNow]);
  const addedKinds = new Set(staffEntries.filter((r) => !r.old_key).map((r) => r.kind));
  const canManageDocs = can('event_participant.register');
  const panelDocs = docRows.map((k) => ({ kind: k, label: DOC_LABELS[k] ?? k, key: documents[k] }));
  const activeDoc = panelDocs.find((d) => d.kind === selectedDoc && d.key)?.kind ?? panelDocs.find((d) => d.key)?.kind ?? null;
  // Public registrations always have documents: give the viewer its own column on wide screens
  const wide = documentEntries.length > 0 || participant.registration_source === 'PUBLIC';
  const sideBySide = wide && isDesktop;

  // The signature is drawn, not uploaded as an image
  const saveDrawnSignature = async (png: Blob) => {
    const key = await uploadRegistrationDocument(eventId, 'signature', png);
    await adminUpdateRegistration(participant.id, { form: {}, documents: { signature: key } });
    setLocalDocs({ ...documents, signature: key });
    setReplacedNow((prev) => new Set(prev).add('signature'));
    queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
    queryClient.invalidateQueries({ queryKey: ['public_registration'] });
    toast({ title: 'Firma guardada' });
  };

  const startUpload = (docKind: DocumentKind) => {
    if (docKind === 'signature') {
      setSignatureOpen(true);
      return;
    }
    pendingKind.current = docKind;
    fileInputRef.current?.click();
  };

  const handleFileChosen = async (file: File | undefined) => {
    const docKind = pendingKind.current;
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file || !docKind) return;
    setUploadingKind(docKind);
    try {
      const blob = await compressImageForUpload(file);
      const key = await uploadRegistrationDocument(eventId, docKind, blob);
      const updated = await replaceRegistrationDocument(participant.id, docKind, key);
      setLocalDocs(updated);
      setReplacedNow((prev) => new Set(prev).add(docKind));
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      toast({ title: `${DOC_LABELS[docKind]} actualizado` });
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : 'No se pudo subir el documento',
        variant: 'destructive',
      });
    } finally {
      setUploadingKind(null);
    }
  };

  const openReceipt = async (id: string) => {
    try {
      setReceipt(await getReceiptById(id));
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo abrir el recibo', variant: 'destructive' });
    }
  };

  const buildMessage = async (status: 'PRE_REGISTERED' | 'VERIFIED' | 'REJECTED', rejectionReason?: string | null) => {
    let editLink: { url: string; expiresAt: string } | null = null;
    if (status === 'REJECTED') {
      // The personal link lets them correct the data; without permission or on error the message just has the form link
      try {
        const link = (await adminGetEditLink(participant.id)) ?? (await adminCreateEditLink(participant.id));
        if (link) editLink = { url: publicEditUrl(link.token), expiresAt: link.expires_at };
      } catch (err) {
        console.error('Could not prepare the edit link for the WhatsApp message', err);
      }
    }
    return reviewMessage({
      status,
      fullName: name,
      eventName: eventName ?? '',
      formUrl: publicFormUrl(participant.public_token),
      rejectionReason,
      editLink,
    });
  };

  const review = async (action: 'VERIFY' | 'REJECT' | 'RESET', opts?: { notify?: boolean }) => {
    setBusy(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc('review_preregistration', {
        p_participant_id: participant.id,
        p_action: action,
        p_reason: action === 'REJECT' ? rejectReason : null,
      });
      if (error) throw new Error(error.message);

      let recorded: { paymentId: string; amount: number; method: EventPaymentMethod } | null = null;
      if (action === 'VERIFY' && alsoRecordPayment && paymentPending) {
        const balance = Math.max(0, price - paid);
        if (balance > 0) {
          const paymentId = await recordPayment({
            participant_id: participant.id,
            payment_amount: balance,
            method: 'bank_transfer',
            voucher_key: documents.voucher ?? null,
            notes: 'Pago verificado con comprobante de preinscripción',
          });
          recorded = { paymentId, amount: balance, method: 'bank_transfer' };
        }
      }

      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.statistics(eventId) });
      queryClient.invalidateQueries({ queryKey: ['participant_payments', participant.id] });
      toast({
        title:
          action === 'VERIFY' ? 'Inscripción verificada' : action === 'REJECT' ? 'Inscripción rechazada' : 'Revisión reiniciada',
      });
      if (action === 'VERIFY') onVerified?.(participant, recorded);
      const finish = () => (queue ? queue.onReviewed() : close());
      if (opts?.notify && action === 'REJECT') {
        // Offer the WhatsApp message first; the dialog moves on when it is closed
        afterNotify.current = finish;
        setNotify(await buildMessage('REJECTED', rejectReason));
      } else {
        finish();
      }
    } catch (err) {
      // Likely a stale view (another admin acted first): refresh so the list shows the real state
      queryClient.invalidateQueries({ queryKey: eventParticipantsKeys.byEvent(eventId) });
      toast({ title: err instanceof Error ? err.message : 'Error al revisar', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Closed while the receipt preview is open: a Radix modal blocks pointer events and treats the preview as "outside" */}
      <Dialog open={!!participant && !receipt} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent className={`max-h-[90vh] overflow-y-auto ${wide ? 'max-w-6xl' : 'max-w-3xl'}`}>
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              {name}
              <Badge variant="outline">
                {kind === 'DELEGATE' ? 'Delegado' : kind === 'GUEST' ? 'Invitado' : 'Ministro'}
              </Badge>
              <Badge variant={status.variant}>{status.text}</Badge>
            </DialogTitle>
            <DialogDescription>
              Registro {participant.registration_source === 'PUBLIC' ? 'por formulario público' : 'por la administración'} del{' '}
              {new Date(participant.registration_date).toLocaleString('es-BO')} · Monto {formatPrice(price)}
            </DialogDescription>
          </DialogHeader>

          {queue && (
            <div className="flex items-center justify-between rounded-md border bg-muted/50 px-3 py-2 text-sm">
              <span className="font-medium">
                Revisión de pendientes: {queue.index + 1} de {queue.total}
              </span>
              <span className="flex gap-1">
                <Button size="sm" variant="outline" onClick={queue.onPrev} disabled={busy || queue.index === 0}>
                  <ChevronLeft className="mr-1 size-4" /> Anterior
                </Button>
                <Button size="sm" variant="outline" onClick={queue.onNext} disabled={busy || queue.index >= queue.total - 1}>
                  Siguiente <ChevronRight className="ml-1 size-4" />
                </Button>
              </span>
            </div>
          )}

          {(onEditForm || onRequestLink) && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
              {needsSignature(participant, signatureMode) ? (
                <span className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <PenLine className="size-4" /> Sin firma: el participante aún no firmó su formulario.
                </span>
              ) : (
                <span className="text-muted-foreground">Corregir o completar el formulario</span>
              )}
              <span className="flex gap-2">
                {onEditForm && (
                  <Button size="sm" variant="outline" onClick={() => onEditForm(participant)}>
                    Editar formulario
                  </Button>
                )}
                {onRequestLink && (
                  <Button size="sm" variant="outline" onClick={() => onRequestLink(participant)}>
                    {needsSignature(participant, signatureMode) ? 'Pedir firma' : 'Enlace de edición'}
                  </Button>
                )}
              </span>
            </div>
          )}

          {participant.registration_status === 'REJECTED' && participant.rejection_reason && (
            <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              Rechazada: {participant.rejection_reason}
            </p>
          )}

          <div className={sideBySide ? 'grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-start gap-4' : undefined}>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="data">Datos</TabsTrigger>
              <TabsTrigger value="documents">Documentos ({documentEntries.length})</TabsTrigger>
              <TabsTrigger value="payments">Pagos y recibos</TabsTrigger>
              <TabsTrigger value="attendance">Asistencia</TabsTrigger>
            </TabsList>

            <TabsContent value="data" className="space-y-4">
              <section>
                <h4 className="mb-2 text-sm font-semibold">Datos enviados</h4>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
                  {FIELD_LABELS.filter(([key]) => data[key]).map(([key, label]) => (
                    <div key={key} className="flex gap-2">
                      <dt className="shrink-0 text-muted-foreground">{label}:</dt>
                      <dd className="truncate">{String(data[key])}</dd>
                    </div>
                  ))}
                  {Object.keys(data).length === 0 && (
                    <p className="text-muted-foreground">Registrado por la administración (sin formulario público).</p>
                  )}
                </dl>
              </section>

              {participant.minister_id && Object.keys(data).length > 0 && (
                <section>
                  <h4 className="mb-2 text-sm font-semibold">Cambios respecto a la ficha del ministro</h4>
                  <MinisterDataMergeSection ministerId={participant.minister_id} snapshot={data} />
                </section>
              )}

              <Button variant="link" className="h-auto p-0" asChild>
                <a href={`/p/${participant.public_token}`} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1 size-4" /> Ver formulario imprimible
                </a>
              </Button>
            </TabsContent>

            <TabsContent value="documents">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                className="sr-only"
                onChange={(e) => handleFileChosen(e.target.files?.[0])}
              />
              <ul className="divide-y rounded-md border text-sm">
                {docRows.map((docKind) => {
                  const key = documents[docKind];
                  return (
                    <li
                      key={docKind}
                      className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 ${activeDoc === docKind ? 'bg-muted/60' : ''}`}
                    >
                      <div>
                        <p className="font-medium">{DOC_LABELS[docKind] ?? docKind}</p>
                        <p className={`text-xs ${key ? 'text-muted-foreground' : 'text-amber-700'}`}>
                          {key
                            ? replacedKinds.has(docKind)
                              ? 'Actualizado por la administración'
                              : addedKinds.has(docKind)
                                ? 'Subido por la administración'
                                : 'Cargado'
                            : 'Falta'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {key && (
                          <Button variant="outline" size="sm" onClick={() => setSelectedDoc(docKind)}>
                            <Eye className="mr-2 size-4" />
                            Ver
                          </Button>
                        )}
                        {canManageDocs && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startUpload(docKind)}
                            disabled={uploadingKind !== null}
                          >
                            {uploadingKind === docKind ? (
                              <Loader2 className="mr-2 size-4 animate-spin" />
                            ) : docKind === 'signature' ? (
                              <PenLine className="mr-2 size-4" />
                            ) : (
                              <Upload className="mr-2 size-4" />
                            )}
                            {docKind === 'signature' ? (key ? 'Firmar de nuevo' : 'Firmar') : key ? 'Reemplazar' : 'Subir'}
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {canManageDocs && (
                <p className="mt-2 text-xs text-muted-foreground">
                  ¿Foto borrosa? Pida una nueva a la persona (por ejemplo por WhatsApp) y use «Reemplazar». El archivo
                  anterior queda guardado como respaldo.
                </p>
              )}
              {/* Narrow screens: the viewer sits under the list */}
              {!sideBySide && documentEntries.length > 0 && (
                <RegistrationDocumentPanel
                  className="mt-3 h-[60vh]"
                  docs={panelDocs}
                  selectedKind={activeDoc}
                  onSelect={setSelectedDoc}
                />
              )}
            </TabsContent>

            <TabsContent value="payments" className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="rounded-md border p-2">
                  <p className="text-xs text-muted-foreground">Monto</p>
                  <p className="font-semibold">{formatPrice(price)}</p>
                </div>
                <div className="rounded-md border p-2">
                  <p className="text-xs text-muted-foreground">Pagado</p>
                  <p className="font-semibold">{formatPrice(paid)}</p>
                </div>
                <div className="rounded-md border p-2">
                  <p className="text-xs text-muted-foreground">Saldo</p>
                  <p className="font-semibold">{formatPrice(Math.max(0, price - paid))}</p>
                </div>
              </div>

              {!canViewPayments ? (
                <Empty>Sin permiso para ver pagos.</Empty>
              ) : loadingPayments ? (
                <Empty>Cargando pagos…</Empty>
              ) : payments.length === 0 ? (
                <Empty>Sin pagos registrados.</Empty>
              ) : (
                <ul className="divide-y rounded-md border text-sm">
                  {payments.map((p) => (
                    <li key={p.id} className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 ${p.status === 'REVERSED' ? 'opacity-60' : ''}`}>
                      <div>
                        <p className="font-medium">
                          {formatPrice(Number(p.amount))}
                          {p.method && <span className="ml-2 font-normal text-muted-foreground">{PAYMENT_METHOD_LABELS[p.method]}</span>}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(p.paid_at).toLocaleString('es-BO')}
                          {p.status === 'REVERSED' && ` · Revertido${p.reversal_reason ? `: ${p.reversal_reason}` : ''}`}
                        </p>
                      </div>
                      {p.receipt ? (
                        <Button variant="link" className="h-auto p-0" onClick={() => openReceipt(p.receipt!.id)}>
                          <FileText className="mr-1 size-4" />
                          N° {formatReceiptNumber(p.receipt.number)}
                          {p.receipt.status !== 'issued' && ' (anulado)'}
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {p.status === 'RECORDED' ? 'Sin recibo' : '—'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>

            <TabsContent value="attendance" className="space-y-3">
              <div className="rounded-md border p-3 text-sm">
                <p>
                  Asistencia al evento:{' '}
                  <strong>
                    {participant.attendance_status === 'ATTENDED'
                      ? 'Asistió'
                      : participant.attendance_status === 'ABSENT'
                        ? 'Ausente'
                        : 'Pendiente'}
                  </strong>
                </p>
                {participant.checked_in_at && (
                  <p className="text-xs text-muted-foreground">
                    Primer ingreso: {new Date(participant.checked_in_at).toLocaleString('es-BO')}
                  </p>
                )}
              </div>
              {loadingAttendance ? (
                <Empty>Cargando asistencia…</Empty>
              ) : sessionAttendance.length === 0 ? (
                <Empty>Sin ingresos a sesiones registrados.</Empty>
              ) : (
                <ul className="divide-y rounded-md border text-sm">
                  {sessionAttendance.map((a, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 px-3 py-2">
                      <span className="font-medium">{a.session?.name ?? 'Sesión'}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(a.checked_in_at).toLocaleString('es-BO')} · {METHOD_LABELS[a.method] ?? a.method}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          </Tabs>

          {sideBySide && (
            <RegistrationDocumentPanel
              className="sticky top-0 h-[64vh]"
              docs={panelDocs}
              selectedKind={activeDoc}
              onSelect={setSelectedDoc}
            />
          )}
          </div>

          {showReject && (
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Motivo del rechazo</Label>
              <Textarea
                id="reject-reason"
                rows={2}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ej. comprobante ilegible, CI no coincide..."
              />
            </div>
          )}

          {!showReject && participant.registration_status !== 'VERIFIED' && paymentPending && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={alsoRecordPayment} onCheckedChange={(c) => setAlsoRecordPayment(c === true)} />
              Registrar pago de {formatPrice(price)} al verificar
            </label>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {!showReject && (
              <Button
                variant="ghost"
                onClick={async () => {
                  afterNotify.current = null;
                  setBusy(true);
                  try {
                    setNotify(await buildMessage(participant.registration_status, participant.rejection_reason));
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy}
              >
                <MessageCircle className="mr-2 size-4" /> WhatsApp
              </Button>
            )}
            {participant.registration_status !== 'PRE_REGISTERED' && (
              <Button variant="ghost" onClick={() => review('RESET')} disabled={busy}>
                Volver a pendiente
              </Button>
            )}
            {showReject ? (
              <>
                <Button variant="outline" onClick={() => setShowReject(false)} disabled={busy}>
                  Cancelar
                </Button>
                <Button variant="destructive" onClick={() => review('REJECT')} disabled={busy || !rejectReason.trim()}>
                  Confirmar rechazo
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => review('REJECT', { notify: true })}
                  disabled={busy || !rejectReason.trim()}
                >
                  <MessageCircle className="mr-2 size-4" /> Rechazar y avisar
                </Button>
              </>
            ) : (
              <>
                {participant.registration_status !== 'REJECTED' && (
                  <Button variant="outline" onClick={() => setShowReject(true)} disabled={busy}>
                    Rechazar
                  </Button>
                )}
                {participant.registration_status !== 'VERIFIED' && (
                  <Button onClick={() => review('VERIFY')} disabled={busy}>
                    {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
                    {queue ? 'Verificar y siguiente' : 'Verificar'}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignatureDialog
        open={signatureOpen}
        onOpenChange={setSignatureOpen}
        title={documents.signature ? 'Firmar de nuevo' : 'Firma del participante'}
        description="Pase la tablet a la persona para que dibuje su firma. La firma anterior queda guardada como respaldo."
        onSave={saveDrawnSignature}
      />

      <WhatsAppMessageDialog
        open={notify !== null}
        onOpenChange={(open) => {
          if (open) return;
          setNotify(null);
          const next = afterNotify.current;
          afterNotify.current = null;
          next?.();
        }}
        title="Avisar al participante"
        recipientName={name}
        phone={participantPhone(participant)}
        defaultMessage={notify ?? ''}
      />

      {receipt && (
        <ReceiptPrintPreview
          data={receiptToPrintData(receipt, publicReceiptUrl(receipt.public_token))}
          share={eventReceiptShare(participant, participantPhone(participant), receipt.number)}
          onClose={() => setReceipt(null)}
        />
      )}
    </>
  );
}
